import React, { useEffect, useState } from "react";
import { Bell, Volume2, VolumeX, Moon } from "lucide-react";

// Preferências por dispositivo (localStorage). Lidas pelo NewTaskAlert.
// Defaults: som ON, modo silêncio noturno OFF.
export const NOTIF_PREFS_KEY = "meo_notif_prefs_v1";

export const loadNotifPrefs = () => {
    try {
        const raw = localStorage.getItem(NOTIF_PREFS_KEY);
        const p = raw ? JSON.parse(raw) : {};
        return {
            sound: p.sound !== false, // default true
            quietNight: !!p.quietNight,
            quietStart: Number.isFinite(p.quietStart) ? p.quietStart : 22,
            quietEnd: Number.isFinite(p.quietEnd) ? p.quietEnd : 8,
        };
    } catch (_) {
        return { sound: true, quietNight: false, quietStart: 22, quietEnd: 8 };
    }
};

const saveNotifPrefs = (p) => {
    localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(p));
    // Notifica outros componentes na mesma tab
    window.dispatchEvent(new Event("meo-notif-prefs-changed"));
};

// Determina se a hora actual está em "modo silêncio"
export const isQuietHours = (prefs = loadNotifPrefs(), now = new Date()) => {
    if (!prefs.quietNight) return false;
    const h = now.getHours();
    const start = prefs.quietStart;
    const end = prefs.quietEnd;
    // Janela atravessa meia-noite (ex: 22→8) → silêncio se h>=start OU h<end
    if (start > end) return h >= start || h < end;
    return h >= start && h < end;
};

export default function NotificationPrefs() {
    const [p, setP] = useState(loadNotifPrefs());

    // sincroniza se outro tab/local mudar
    useEffect(() => {
        const onChange = () => setP(loadNotifPrefs());
        window.addEventListener("storage", onChange);
        window.addEventListener("meo-notif-prefs-changed", onChange);
        return () => {
            window.removeEventListener("storage", onChange);
            window.removeEventListener("meo-notif-prefs-changed", onChange);
        };
    }, []);

    const update = (patch) => {
        const next = { ...p, ...patch };
        setP(next);
        saveNotifPrefs(next);
    };

    return (
        <div className="kpi-card">
            <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-[#002FE6]" />
                <div className="overline">Notificações de novas tarefas</div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                Estas preferências aplicam-se apenas a este dispositivo.
            </p>

            {/* Toggle: Som */}
            <label className="flex items-center justify-between gap-3 py-3 border-b border-zinc-100 cursor-pointer">
                <div className="flex items-center gap-3">
                    {p.sound ? (
                        <Volume2 size={18} className="text-emerald-600" />
                    ) : (
                        <VolumeX size={18} className="text-zinc-400" />
                    )}
                    <div>
                        <div className="text-sm font-semibold">Som de alerta</div>
                        <div className="text-xs text-zinc-500">
                            Bip suave quando aparece uma tarefa nova
                        </div>
                    </div>
                </div>
                <input
                    type="checkbox"
                    data-testid="notif-sound-toggle"
                    checked={p.sound}
                    onChange={(e) => update({ sound: e.target.checked })}
                    className="w-5 h-5 accent-[#002FE6] cursor-pointer"
                />
            </label>

            {/* Toggle: Modo silêncio noturno */}
            <label className="flex items-center justify-between gap-3 py-3 cursor-pointer">
                <div className="flex items-center gap-3">
                    <Moon size={18} className={p.quietNight ? "text-violet-600" : "text-zinc-400"} />
                    <div>
                        <div className="text-sm font-semibold">Modo silêncio noturno</div>
                        <div className="text-xs text-zinc-500">
                            Não mostra popup nem som entre {String(p.quietStart).padStart(2, "0")}h
                            {" – "}
                            {String(p.quietEnd).padStart(2, "0")}h
                        </div>
                    </div>
                </div>
                <input
                    type="checkbox"
                    data-testid="notif-quiet-toggle"
                    checked={p.quietNight}
                    onChange={(e) => update({ quietNight: e.target.checked })}
                    className="w-5 h-5 accent-violet-600 cursor-pointer"
                />
            </label>

            {p.quietNight && (
                <div className="grid grid-cols-2 gap-3 mt-2 pt-3 border-t border-zinc-100">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                            Início
                        </label>
                        <select
                            data-testid="notif-quiet-start"
                            value={p.quietStart}
                            onChange={(e) => update({ quietStart: Number(e.target.value) })}
                            className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-md text-sm bg-white"
                        >
                            {Array.from({ length: 24 }).map((_, h) => (
                                <option key={h} value={h}>
                                    {String(h).padStart(2, "0")}:00
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                            Fim
                        </label>
                        <select
                            data-testid="notif-quiet-end"
                            value={p.quietEnd}
                            onChange={(e) => update({ quietEnd: Number(e.target.value) })}
                            className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-md text-sm bg-white"
                        >
                            {Array.from({ length: 24 }).map((_, h) => (
                                <option key={h} value={h}>
                                    {String(h).padStart(2, "0")}:00
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
}
